import { useRefusalAnnouncementStore } from '@/lib/stores/refusal-announcement-store';

/*
 * RefusalAnnouncer (composed; E01.F03b.R60 — AC-8b85b662 frontend half, honours
 * AC-52585c58).
 *
 * The global live-region host that voices an attempted-and-refused act to a
 * screen reader. The 401/403 interceptor in app/query-client.ts announces the
 * one generic refusal into the client-only refusal-announcement store (outside
 * React); this host — mounted once in the shell, like Toaster — renders whatever
 * the store holds as an ASSERTIVE live region (role="alert", implicitly
 * aria-live="assertive"), so a screen-reader user HEARS the refusal (OQ-E01-04,
 * Rick Barker 2026-10-05; the global analogue of F01's role="alert" sign-in
 * refusal, AC-91b2d2d6).
 *
 * When the store is silent — no attempt, e.g. a withheld control — the host
 * renders nothing to announce: a hidden control surfaces no reason, silence not
 * a message (OQ-E01-04).
 *
 * Domain-free: it reads only the client-only store, no props, no fetch, no
 * domain words (AGENTS.md components/composed rule). It is visually hidden —
 * the refusal is a screen-reader announcement, not a visible banner (the
 * enterprise-restrained surface shows no decorative notice); it rides the one
 * interceptor, never an inline `if (status === …)` in a component.
 */
export function RefusalAnnouncer() {
  const message = useRefusalAnnouncementStore((state) => state.message);
  const nonce = useRefusalAnnouncementStore((state) => state.nonce);

  // The live region (role="alert") stays mounted so it is a stable announcement
  // region; the message is rendered in a child KEYED on the monotonic nonce, so
  // a second identical refusal remounts that child — a genuine DOM change the
  // live region re-announces (finding 13). Without the key, a repeat refusal
  // writes the same string, the DOM does not change, and assistive tech says
  // nothing.
  return (
    <div role="alert" className="sr-only">
      {message ? <span key={nonce}>{message}</span> : ''}
    </div>
  );
}
